// Oike — the commercial side of a person, managed from their card (Personas).
//
// Opportunities are commercial records: they are written only through the
// commercial path (the browser → airtable-proxy, with the permissions of whoever
// is signed in), never by the coaching functions. This module only builds and
// validates the fields, so the card and the tests write exactly the same.
//
// Field names are the ones the commercial screens already write in the central
// base (CPBriefings › opportunity form, requestOrigin › opportunity from a
// request). `Contact` is new: the link to the one contact the opportunity is for
// (Stakeholders, by id). Without it an opportunity is only of its company, and
// it is never attributed to a person by name (Opportunities › Stakeholders is a
// text with a name, not an identifier).
import { OPP_STAGES, CLOSED_STAGES } from './opportunityStages.js';

export const OPPORTUNITY = {
  name: 'Deal/Opp name', stage: 'Stage', nextStep: 'Next step', value: 'Value', closeDate: 'close date',
  openingDate: 'Opening date', reason: 'Reason', account: 'Account', originRequest: 'Origin Request',
  contact: 'Contact', // new (Omni): link to Stakeholders, one contact
};
export const STAGES = OPP_STAGES;
export const isClosedStage = (stage) => CLOSED_STAGES.includes(stage);
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const REC_RE = /^rec[A-Za-z0-9]{14}$/;

// Real types in the central base (confirmed by Omni, 2026-10): Deal/Opp name, Stage and Next step are
// single line texts (Stage has no predefined options: Oike offers its own list, STAGES, but an existing
// value outside it is kept as it is); Value is a currency; close date is a date with time (UTC), so a
// day is written at noon UTC; Opening date is a day; Reason is a long text; Account and Contact hold one
// record each (checked again by the server, shared/opportunityRules.ts).
const line = (v, max) => String(v ?? '').replace(/\s+/g, ' ').trim().slice(0, max);
const noonUtc = (day) => `${day}T12:00:00.000Z`;
const dayOf = (v) => (v ? String(v).slice(0, 10) : '');

function checkName(form) {
  const name = line(form.name, 200);
  if (!name) throw new Error('Falta el nombre de la oportunidad');
  return name;
}
function checkStage(stage) {
  const v = line(stage, 100);
  if (!STAGES.includes(v)) throw new Error('Etapa no válida');
  return v;
}
function checkValue(v) {
  if (v === '' || v === null || v === undefined) return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) throw new Error('Valor no válido');
  return Math.round(n * 100) / 100;
}
function checkDay(v) {
  if (!v) return null;
  if (!DAY_RE.test(v)) throw new Error('Fecha de cierre no válida');
  return v;
}

/** A new opportunity for this person: linked to their contact by id, and to a company only if one is chosen. */
export function newOpportunityFields(form, { stakeholderId, accountId = null, today = new Date().toISOString().slice(0, 10) }) {
  if (!REC_RE.test(String(stakeholderId || ''))) throw new Error('La persona no tiene un contacto comercial vinculado');
  const f = {
    [OPPORTUNITY.name]: checkName(form), [OPPORTUNITY.stage]: checkStage(form.stage || 'Discovery'),
    [OPPORTUNITY.openingDate]: today, [OPPORTUNITY.contact]: [stakeholderId],
  };
  if (line(form.nextStep, 500)) f[OPPORTUNITY.nextStep] = line(form.nextStep, 500);
  if (String(form.reason || '').trim()) f[OPPORTUNITY.reason] = String(form.reason).trim().slice(0, 2000);
  const value = checkValue(form.value);
  if (value !== null) f[OPPORTUNITY.value] = value;
  const close = checkDay(form.closeDate);
  if (close) f[OPPORTUNITY.closeDate] = noonUtc(close);
  if (accountId) {
    if (!REC_RE.test(accountId)) throw new Error('Empresa no válida');
    f[OPPORTUNITY.account] = [accountId];
  }
  return f;
}

/**
 * Changes to an opportunity of this person: only what differs from `original`
 * (what the card read), so editing one thing never rewrites the others. An
 * existing stage outside Oike's list is kept unless it is changed on purpose.
 * Never its company, its origin or its contact.
 */
export function editOpportunityFields(form, original = {}) {
  const f = {};
  const name = checkName(form);
  if (name !== line(original.name, 200)) f[OPPORTUNITY.name] = name;
  if (form.stage !== undefined && line(form.stage, 100) !== line(original.stage, 100)) f[OPPORTUNITY.stage] = checkStage(form.stage);
  if (form.nextStep !== undefined && line(form.nextStep, 500) !== line(original.nextStep, 500)) f[OPPORTUNITY.nextStep] = line(form.nextStep, 500);
  if (form.reason !== undefined && String(form.reason || '').trim() !== String(original.reason || '').trim()) f[OPPORTUNITY.reason] = String(form.reason || '').trim().slice(0, 2000);
  if (form.value !== undefined) {
    const value = checkValue(form.value);
    const before = original.value === undefined || original.value === null || original.value === '' ? null : Number(original.value);
    if (value !== before) f[OPPORTUNITY.value] = value;
  }
  if (form.closeDate !== undefined) {
    const close = checkDay(form.closeDate);
    if ((close || '') !== dayOf(original.closeDate)) f[OPPORTUNITY.closeDate] = close ? noonUtc(close) : null;
  }
  return f;
}

/** Attribute an opportunity of the company (with no contact yet) to this person: only the link, on purpose. */
export function contactLinkFields(stakeholderId) {
  if (!REC_RE.test(String(stakeholderId || ''))) throw new Error('Contacto no válido');
  return { [OPPORTUNITY.contact]: [stakeholderId] };
}

/** A new commercial contact from the person's card (name, last name, email); the card links it after. */
export function contactFromPerson(person) {
  const parts = String(person.name || '').trim().split(/\s+/);
  const f = { 'Name': parts[0] || 'Sin nombre' };
  if (parts.length > 1) f['Last name'] = parts.slice(1).join(' ');
  if (person.email) f['Email'] = String(person.email).trim().toLowerCase();
  return f;
}
